import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiError, listFindings } from "../services/AiService";
import { CLASSIFICATIONS, FindingReviewStatus, FindingSummary, RiskLevel } from "../types/ai";
import { ClassificationChip, ErrorAlert, Loading, PageShell, Pager, PAGE_SIZES, ReviewChip, RiskChip } from "../component/aces/Shared";

const REVIEW_STATUSES: FindingReviewStatus[] = ["AI_PROPOSED", "UNDER_REVIEW", "ACCEPTED", "MODIFIED", "REJECTED", "CLOSED"];
const RISKS: RiskLevel[] = ["CRITICAL", "HIGH", "MODERATE", "LOW", "NOT_DETERMINED"];

/** Every finding in the company, independent of the analysis it came from. */
const FindingsRegisterScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<FindingSummary[] | null>(null);
  const [status, setStatus] = useState("");
  const [classification, setClassification] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZES[0]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(null);
    setPage(0);
    listFindings({ status: status || undefined, classification: classification || undefined, riskLevel: riskLevel || undefined, urgent: urgent || undefined })
      .then(setItems)
      .catch((e) => {
        setError(apiError(e));
        setItems([]);
      });
  }, [status, classification, riskLevel, urgent]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const newestFirst = [...(items || [])].sort((a, b) => Number(b.isUrgent) - Number(a.isUrgent) || b.id - a.id);
    if (!q) return newestFirst;
    return newestFirst.filter((f) => [f.findingRef, f.statement, f.analysisRef, f.checklistRef, f.checklistTitle].some((v) => v?.toLowerCase().includes(q)));
  }, [items, search]);

  return (
    <PageShell title={t("aces.nav.findings")}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      <div className="aces-filters">
        <div className="aces-field">
          <label htmlFor="findings-search">{t("aces.nav.findings")}</label>
          <input id="findings-search" className="form-control" placeholder={t("aces.register.searchFindings")} value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        </div>
        <div className="aces-field">
          <label htmlFor="findings-status">{t("aces.finding.reviewStatus")}</label>
          <select id="findings-status" className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t("aces.register.allStatuses")}</option>
            {REVIEW_STATUSES.map((s) => <option key={s} value={s}>{t(`aces.enum.review.${s}`)}</option>)}
          </select>
        </div>
        <div className="aces-field">
          <label htmlFor="findings-class">{t("aces.finding.classification")}</label>
          <select id="findings-class" className="form-control" value={classification} onChange={(e) => setClassification(e.target.value)}>
            <option value="">{t("aces.register.allClassifications")}</option>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{t(`aces.enum.classification.${c}`)}</option>)}
          </select>
        </div>
        <div className="aces-field">
          <label htmlFor="findings-risk">{t("aces.finding.risk")}</label>
          <select id="findings-risk" className="form-control" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
            <option value="">{t("aces.register.allRisks")}</option>
            {RISKS.map((r) => <option key={r} value={r}>{t(`aces.enum.risk.${r}`)}</option>)}
          </select>
        </div>
        <label className="aces-check">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> {t("aces.register.urgentOnly")}
        </label>
      </div>
      {items === null ? <Loading /> : (
        <>
          <div className="__defaultTable aces-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("aces.finding.ref")}</th>
                  <th>{t("aces.finding.statement")}</th>
                  <th>{t("aces.analysis.checklist")}</th>
                  <th>{t("aces.register.analysis")}</th>
                  <th>{t("aces.finding.classification")}</th>
                  <th>{t("aces.finding.risk")}</th>
                  <th>{t("aces.finding.reviewStatus")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr><td colSpan={7}>{t("aces.register.noFindings")}</td></tr>
                ) : visible.slice(page * size, (page + 1) * size).map((f) => (
                  <tr key={f.id}>
                    <td>
                      <button type="button" className="aces-table-link" onClick={() => navigate(`/ai/finding/${f.id}`)}>{f.findingRef}</button>
                      {f.isUrgent && <div><span className="aces-chip darkred">{t("aces.register.critical")}</span></div>}
                    </td>
                    <td>{f.statement}</td>
                    <td>{f.checklistTitle}<div className="aces-muted">{f.checklistRef}</div></td>
                    <td><button type="button" className="aces-table-link" onClick={() => navigate(`/ai/analysis/${f.analysisId}`)}>{f.analysisRef}</button></td>
                    <td><ClassificationChip value={f.classification} /></td>
                    <td><RiskChip level={f.riskLevel} score={f.riskScore} /></td>
                    <td><ReviewChip value={f.reviewStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} size={size} total={visible.length} onPage={setPage} onSize={(s) => { setSize(s); setPage(0); }} />
        </>
      )}
    </PageShell>
  );
};

export default FindingsRegisterScreen;
